/*!
 * AudoChat - Chat Page
 */

import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";

// @mui material components
import { IconButton, TextField, Avatar, Chip, Card, useMediaQuery, useTheme, Grid, CircularProgress } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Vision UI Dashboard React context
import { useVisionUIController, setMiniSidenav } from "context";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// React icons
import { BsChatDots, BsRobot } from "react-icons/bs";
import { IoMdSend } from "react-icons/io";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { IoArrowBack } from "react-icons/io5";
import { MdDone, MdDoneAll, MdSchedule, MdError } from "react-icons/md";

// API
import api from "utils/api";
import auth from "utils/auth";
import { supabase } from "lib/supabase";
import { subscribeToConversations, subscribeToMessages, subscribeToAllMessages } from "utils/realtime";

const { secondary, info, text, dark, gradients, borderCol, error, success, orange, primary } = colors;
const { card } = gradients;

// Helper function to get date components في توقيت البحرين (سنة/شهر/يوم فقط)
const getBahrainDateComponents = (date) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Bahrain",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  const parts = formatter.formatToParts(date);
  const year = parseInt(parts.find((p) => p.type === "year").value, 10);
  const month = parseInt(parts.find((p) => p.type === "month").value, 10) - 1; // 0-indexed
  const day = parseInt(parts.find((p) => p.type === "day").value, 10);

  return { year, month, day };
};

// Helper function to format timestamp with date (Bahrain timezone)
// أفضل منطق: يقارن تواريخ البحرين مباشرة لـ Today / Yesterday
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const now = new Date();

  // مكوّنات التاريخ في توقيت البحرين
  const nowComponents = getBahrainDateComponents(now);
  const msgComponents = getBahrainDateComponents(date);

  // وقت الرسالة في توقيت البحرين
  const timeString = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Bahrain",
  });

  // هل التاريخ هو اليوم في البحرين؟
  const isToday =
    msgComponents.year === nowComponents.year &&
    msgComponents.month === nowComponents.month &&
    msgComponents.day === nowComponents.day;

  if (isToday) {
    return `Today ${timeString}`;
  }

  // نحسب "أمس" في البحرين عن طريق طرح يوم واحد من منتصف ليل اليوم في البحرين
  const todayBahrainMidnightUtc = new Date(
    Date.UTC(nowComponents.year, nowComponents.month, nowComponents.day)
  );
  const yesterdayBahrainMidnightUtc = new Date(
    todayBahrainMidnightUtc.getTime() - 24 * 60 * 60 * 1000
  );
  const yesterdayComponents = getBahrainDateComponents(yesterdayBahrainMidnightUtc);

  const isYesterday =
    msgComponents.year === yesterdayComponents.year &&
    msgComponents.month === yesterdayComponents.month &&
    msgComponents.day === yesterdayComponents.day;

  if (isYesterday) {
    return `Yesterday ${timeString}`;
  }

  // أقدم من أمس: نعرض التاريخ + الوقت في توقيت البحرين
  const dateString = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: msgComponents.year !== nowComponents.year ? "numeric" : undefined,
    timeZone: "Asia/Bahrain",
  });

  return `${dateString} ${timeString}`;
};

// Helper function to detect if text contains Arabic characters
const containsArabic = (text) => {
  if (!text) return false;
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

// Helper function to get text direction based on content
const getTextDirection = (text) => {
  return containsArabic(text) ? 'rtl' : 'ltr';
};

function Chat() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav } = controller;

  // Add pulse animation for sending status
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState("");
  const [isAIMode, setIsAIMode] = useState(false);
  const [showConversations, setShowConversations] = useState(true);
  const [showNavbar, setShowNavbar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [messageStatuses, setMessageStatuses] = useState({}); // Track message sending status: { messageId: 'sending' | 'sent' | 'failed' }
  const [messageErrors, setMessageErrors] = useState({}); // Track error messages for failed messages: { messageId: 'error message' }
  const [inputDirection, setInputDirection] = useState('ltr'); // Track input direction based on content
  
  const messagesEndRef = useRef(null);
  const previousConversationIdRef = useRef(null);
  const conversationsSubscriptionRef = useRef(null);
  const messagesSubscriptionRef = useRef(null);
  const globalMessagesSubscriptionRef = useRef(null);
  const selectedConversationRef = useRef(null); // Ref to track current selected conversation

  // Load conversations from database and subscribe to realtime updates
  // This effect runs only once on mount - subscription stays active regardless of selected conversation
  useEffect(() => {
    loadConversations();

    // Subscribe to conversations changes - this subscription stays active
    conversationsSubscriptionRef.current = subscribeToConversations((payload) => {
      console.log('Conversation change:', payload);
      
      if (payload.eventType === 'INSERT') {
        // New conversation added
        const baseTimestamp = payload.new.updated_at || payload.new.created_at || new Date().toISOString();
        const newConv = {
          ...payload.new,
          aiMode: payload.new.ai_mode !== undefined ? payload.new.ai_mode : true,  // Default to TRUE
          messages: [],
          timestamp: baseTimestamp,
          formattedTimestamp: formatTimestamp(baseTimestamp),
        };
        // Add to top and sort by updated_at/timestamp
        setConversations((prev) => {
          const updated = [newConv, ...prev];
          updated.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
          });
          return updated;
        });
      } else if (payload.eventType === 'UPDATE') {
        // Conversation updated (AI mode, unread, last_message, etc.)
        const baseTimestamp = payload.new.updated_at || payload.new.created_at || new Date().toISOString();

        setConversations((prev) => {
          const updated = prev.map((conv) =>
            conv.id === payload.new.id
              ? {
                  ...conv,
                  ...payload.new,
                  aiMode: payload.new.ai_mode !== undefined ? payload.new.ai_mode : true,  // Default to TRUE
                  messages: conv.messages || [],
                  timestamp: baseTimestamp,
                  formattedTimestamp: formatTimestamp(baseTimestamp),
                }
              : conv
          );
          // Re-sort by updated_at/timestamp (most recent first)
          updated.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
          });
          return updated;
        });

        // Update selected conversation if it's the one being updated
        // Use functional update to get current selectedConversation
        setSelectedConversation((currentSelected) => {
          if (currentSelected?.id === payload.new.id) {
            return {
              ...currentSelected,
              ...payload.new,
              aiMode: payload.new.ai_mode || false,
            };
          }
          return currentSelected;
        });
      } else if (payload.eventType === 'DELETE') {
        // Conversation deleted
        setConversations(prev => prev.filter(conv => conv.id !== payload.old.id));
        
        // Clear selected conversation if it was deleted
        // Use functional update to get current selectedConversation
        setSelectedConversation((currentSelected) => {
          if (currentSelected?.id === payload.old.id) {
            return null;
          }
          return currentSelected;
        });
      }
    });

    // Cleanup subscription on unmount only
    return () => {
      if (conversationsSubscriptionRef.current) {
        try {
          supabase.removeChannel(conversationsSubscriptionRef.current);
        } catch (error) {
          console.warn('Error cleaning up conversations subscription:', error);
        }
      }
    };
  }, []); // Empty dependency array - runs only once on mount

  const loadConversations = async () => {
    try {
      setLoading(true);
      const conversations = await api.getConversations();
      // Transform conversations to match expected format
      // conversations are already sorted by updated_at from api.getConversations()
      const transformedConversations = (conversations || []).map((conv) => ({
        ...conv,
        aiMode: conv.ai_mode !== undefined ? conv.ai_mode : true,  // Default to TRUE
        messages: [], // Will be loaded when conversation is selected
        // Use updated_at (from conversations table) for sorting and display
        timestamp: conv.updated_at || conv.created_at || new Date().toISOString(),
        formattedTimestamp: formatTimestamp(conv.updated_at || conv.created_at || new Date().toISOString()),
      }));
      
      // Conversations are already sorted by api.getConversations(), but sort again to be safe
      transformedConversations.sort((a, b) => {
        const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
        const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
        return timeB - timeA;
      });
      
      setConversations(transformedConversations);
    } catch (error) {
      console.error("Error loading conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Global messages subscription: أي رسالة جديدة في أي محادثة
  // نحدّث بها قائمة المحادثات (آخر رسالة + توقيت + ترتيب)
  // This subscription stays active regardless of selected conversation
  useEffect(() => {
    globalMessagesSubscriptionRef.current = subscribeToAllMessages((payload) => {
      if (payload.eventType !== "INSERT") return;

      const msg = payload.new;
      if (!msg || !msg.conversation_id) return;

      const text =
        msg.message ||
        msg.content ||
        msg.text ||
        msg.body ||
        "";
      const msgTimestamp =
        msg.created_at ||
        msg.timestamp ||
        new Date().toISOString();

      // Use ref to access current selectedConversation without dependency
      const currentSelected = selectedConversationRef.current;
      
      setConversations((prev) => {
        if (!prev || prev.length === 0) return prev;

        let found = false;
        const updated = prev.map((conv) => {
          if (conv.id === msg.conversation_id) {
            found = true;
            const isOpen = currentSelected?.id === conv.id;
            const isUserMessage = msg.sender === "user";

            return {
              ...conv,
              last_message: text || conv.last_message,
              timestamp: msgTimestamp,
              updated_at: msgTimestamp,
              formattedTimestamp: formatTimestamp(msgTimestamp),
              unread: isOpen
                ? conv.unread || 0
                : isUserMessage
                ? (conv.unread || 0) + 1
                : conv.unread || 0,
            };
          }
          return conv;
        });

        if (!found) {
          // المحادثة غير موجودة في القائمة (مثلاً أتت من ويبهوك قبل فتحها)
          // نترك القائمة كما هي حتى يتم تحميلها من API
          return prev;
        }

        // إعادة الترتيب حسب آخر توقيت
        updated.sort((a, b) => {
          const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
          return timeB - timeA;
        });

        return updated;
      });
    });

    return () => {
      if (globalMessagesSubscriptionRef.current) {
        try {
          supabase.removeChannel(globalMessagesSubscriptionRef.current);
        } catch (error) {
          console.warn('Error cleaning up global messages subscription:', error);
        }
      }
    };
  }, []); // Empty dependency array - runs only once on mount

  // Load conversation details when selected and subscribe to messages
  useEffect(() => {
    if (selectedConversation?.id) {
      // Clear unread count when conversation is opened
      setConversations(prev =>
        prev.map(conv =>
          conv.id === selectedConversation.id
            ? { ...conv, unread: 0 }
            : conv
        )
      );
      
      loadConversationDetails(selectedConversation.id);

      // Unsubscribe from previous messages subscription
      if (messagesSubscriptionRef.current) {
        try {
          supabase.removeChannel(messagesSubscriptionRef.current);
        } catch (error) {
          console.warn('Error removing previous messages subscription:', error);
        }
      }

      // Subscribe to messages changes for this conversation
      messagesSubscriptionRef.current = subscribeToMessages(selectedConversation.id, (payload) => {
        console.log('Message change:', payload);
        
        if (payload.eventType === 'INSERT') {
          // New message added
          // Determine sender: use sender field if exists, otherwise default based on user_id
          let sender = payload.new.sender;
          if (!sender || sender === null || sender === undefined) {
            // If no sender field, check user_id: if user_id exists, it's a user message
            sender = payload.new.user_id ? 'user' : 'agent';
          }
          const newMessage = {
            id: payload.new.id,
            text: payload.new.message || payload.new.content || payload.new.text || payload.new.body || '',
            sender: sender,
            time: formatTimestamp(payload.new.created_at || payload.new.timestamp),
            created_at: payload.new.created_at || payload.new.timestamp, // Keep original timestamp for 24h check
            timestamp: payload.new.timestamp || payload.new.created_at,
          };
          
          // Set default status for new messages (sent for agent/ai, no status for user)
          if (newMessage.sender === 'agent' || newMessage.sender === 'ai') {
            setMessageStatuses(prev => {
              // Only set if not already set (to preserve sending/failed status)
              if (!prev[newMessage.id]) {
                return { ...prev, [newMessage.id]: 'sent' };
              }
              return prev;
            });
          }
          
          setSelectedConversation(prev => {
            if (prev && prev.id === payload.new.conversation_id) {
              const updated = {
                ...prev,
                messages: [...(prev.messages || []), newMessage],
              };
              // Scroll to bottom immediately when new message arrives
              setTimeout(() => {
                if (messagesEndRef.current) {
                  messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
                }
              }, 0);
              return updated;
            }
            return prev;
          });
        } else if (payload.eventType === 'UPDATE') {
          // Message updated
          const updatedMessage = {
            id: payload.new.id,
            text: payload.new.message || payload.new.content || payload.new.text || payload.new.body || '',
            sender: payload.new.sender || (payload.new.user_id ? 'user' : 'bot'),
            time: formatTimestamp(payload.new.created_at || payload.new.timestamp),
          };
          
          setSelectedConversation(prev => {
            if (prev && prev.messages) {
              return {
                ...prev,
                messages: prev.messages.map(msg =>
                  msg.id === payload.new.id ? updatedMessage : msg
                ),
              };
            }
            return prev;
          });
        } else if (payload.eventType === 'DELETE') {
          // Message deleted
          setSelectedConversation(prev => {
            if (prev && prev.messages) {
              return {
                ...prev,
                messages: prev.messages.filter(msg => msg.id !== payload.old.id),
              };
            }
            return prev;
          });
        }
      });
    }

    // Cleanup messages subscription on unmount or conversation change
    return () => {
      if (messagesSubscriptionRef.current) {
        try {
          supabase.removeChannel(messagesSubscriptionRef.current);
        } catch (error) {
          console.warn('Error cleaning up messages subscription:', error);
        }
      }
    };
  }, [selectedConversation?.id]);

  const loadConversationDetails = async (conversationId) => {
    // Initialize message statuses for loaded messages (default to 'sent' for agent/ai messages)
    const initializeMessageStatuses = (messages) => {
      const statuses = {};
      messages?.forEach(msg => {
        if ((msg.sender === 'agent' || msg.sender === 'ai') && msg.id) {
          statuses[msg.id] = 'sent'; // Default to sent for loaded messages
        }
      });
      setMessageStatuses(prev => ({ ...prev, ...statuses }));
    };
    try {
      const conv = await api.getConversationById(conversationId);
      if (conv) {
        // Load messages for this conversation
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true });
        
        // Transform messages - ensure we handle both array and object formats
        const messagesArray = messages || [];
        const transformedMessages = messagesArray.map((msg) => {
          // Determine sender: use sender field if exists, otherwise default based on user_id
          let sender = msg.sender;
          if (!sender || sender === null || sender === undefined) {
            // If no sender field, check user_id: if user_id exists, it's a user message
            sender = msg.user_id ? 'user' : 'agent';
          }
          return {
            id: msg.id,
            text: msg.message || msg.content || msg.text || msg.body || '',
            sender: sender,
            time: formatTimestamp(msg.created_at || msg.timestamp),
            created_at: msg.created_at || msg.timestamp, // Keep original timestamp for 24h check
            timestamp: msg.timestamp || msg.created_at,
          };
        });
        
        console.log('Loaded conversation:', conv);
        console.log('Transformed messages:', transformedMessages);
        
        // Initialize message statuses for loaded messages
        initializeMessageStatuses(transformedMessages);
        
        const updatedConversation = {
          ...conv,
          aiMode: conv.ai_mode !== undefined ? conv.ai_mode : true,  // Default to TRUE
          messages: transformedMessages,
        };
        setSelectedConversation(updatedConversation);
        // Scroll to bottom when conversation is loaded
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading conversation details:", error);
    }
  };

  // Update ref when selectedConversation changes
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Update AI mode when conversation changes (only when conversation ID changes)
  useEffect(() => {
    const currentId = selectedConversation?.id;

    if (currentId !== previousConversationIdRef.current) {
      previousConversationIdRef.current = currentId;

      if (selectedConversation) {
        const newAIMode = selectedConversation.aiMode || false;

        if (isAIMode !== newAIMode) {
          setIsAIMode(newAIMode);
        }

        if (showConversations) {
          setShowConversations(false);
        }
      } else {
        if (!showConversations) {
          setShowConversations(true);
        }

        if (isAIMode !== false) {
          setIsAIMode(false);
        }
      }
    }
  }, [selectedConversation]);

  // Scroll to bottom when messages change - instant scroll for real-time updates
  useEffect(() => {
    if (messagesEndRef.current) {
      // Use instant scroll for real-time updates
      messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
    }
  }, [selectedConversation?.messages]);

  // Check if we can reply (24-hour WhatsApp window)
  const canReplyToConversation = () => {
    if (!selectedConversation || !selectedConversation.messages || selectedConversation.messages.length === 0) {
      return true; // New conversation, can reply
    }

    // Find the last message from user
    const userMessages = selectedConversation.messages.filter(msg => msg.sender === "user");
    if (userMessages.length === 0) {
      return true; // No user messages, can reply
    }

    // Get the most recent user message
    const lastUserMessage = userMessages[userMessages.length - 1];
    
    // Try to get timestamp from message (check multiple possible fields)
    let lastMessageTime = null;
    
    // Check if message has created_at (from database)
    if (lastUserMessage.created_at) {
      lastMessageTime = new Date(lastUserMessage.created_at);
    } else if (lastUserMessage.timestamp) {
      lastMessageTime = new Date(lastUserMessage.timestamp);
    } else if (selectedConversation.timestamp) {
      lastMessageTime = new Date(selectedConversation.timestamp);
    } else if (lastUserMessage.time) {
      // Fallback: try to parse time string (format: "03:13 PM")
      // This is less accurate but better than nothing
      try {
        const now = new Date();
        const [time, period] = lastUserMessage.time.split(" ");
        if (time && period) {
          const [hours, minutes] = time.split(":");
          let hour24 = parseInt(hours);
          if (period === "PM" && hour24 !== 12) hour24 += 12;
          if (period === "AM" && hour24 === 12) hour24 = 0;
          lastMessageTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour24, parseInt(minutes));
        }
      } catch (e) {
        console.error("Error parsing time:", e);
      }
    }

    if (!lastMessageTime || isNaN(lastMessageTime.getTime())) {
      return true; // Can't determine time, allow reply to be safe
    }

    // Calculate hours difference
    const now = new Date();
    const hoursDiff = (now - lastMessageTime) / (1000 * 60 * 60);

    // WhatsApp allows replies within 24 hours
    return hoursDiff <= 24;
  };

  // Function to retry sending a failed message
  const handleRetryMessage = async (messageId, messageText, sender) => {
    if (!selectedConversation) return;

    const currentUser = auth.getCurrentUser();

    try {
      // Set status to sending
      setMessageStatuses(prev => ({ ...prev, [messageId]: 'sending' }));
      // Clear error
      setMessageErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[messageId];
        return newErrors;
      });

      // Prepare data for WhatsApp API
      const whatsappPayload = {
        message: messageText,
        sender: {
          id: currentUser?.id || 'unknown',
          name: currentUser?.name || 'Agent',
          num: currentUser?.phone || currentUser?.email || '',
          email: currentUser?.email || ''
        },
        recipient: {
          id: selectedConversation.id?.toString() || '',
          name: selectedConversation.name || 'Customer',
          num: selectedConversation.phone || '',
          email: selectedConversation.email || ''
        }
      };

      // Send to WhatsApp API
      try {
        const response = await fetch('https://api.audo.chat/webhook/team-msg', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(whatsappPayload),
        });

        // Check if response is OK (status 200-299)
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: Failed to send message`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          }
          
          console.error("WhatsApp API error (retry):", response.status, errorMessage);
          setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
          setMessageErrors(prev => ({ ...prev, [messageId]: errorMessage }));
          return;
        }

        const result = await response.json();

        // Handle different response formats
        if (
          result.status === 'done' || 
          result.status === 'success' || 
          result.success === true ||
          result.message === 'sent' ||
          (response.status >= 200 && response.status < 300 && !result.error)
        ) {
          // Message sent successfully
          setMessageStatuses(prev => ({ ...prev, [messageId]: 'sent' }));
          setMessageErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[messageId];
            return newErrors;
          });
        } else {
          const errorMessage = result.error || result.message || 'Unknown error occurred';
          console.error("WhatsApp API returned error (retry):", result);
          setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
          setMessageErrors(prev => ({ ...prev, [messageId]: errorMessage }));
        }
      } catch (apiError) {
        const errorMessage = apiError.message || 'Network error or server unavailable';
        console.error("WhatsApp API error (retry):", apiError);
        setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
        setMessageErrors(prev => ({ ...prev, [messageId]: errorMessage }));
      }
    } catch (error) {
      console.error("Error retrying message:", error);
      setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
      setMessageErrors(prev => ({ ...prev, [messageId]: error.message || 'Failed to retry message' }));
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation) return;

    // Check WhatsApp 24-hour window restriction
    if (!canReplyToConversation()) {
      alert("⚠️ WhatsApp Restriction: You cannot reply to this conversation because the last customer message was sent more than 24 hours ago. Please use a template message to start a new conversation.");
      return;
    }

    const messageText = message.trim();
    const sender = isAIMode ? "ai" : "agent";
    const currentUser = auth.getCurrentUser();
    const tempMessageId = `temp-${Date.now()}-${Math.random()}`;

    try {
      // Clear input immediately after pressing send
      setMessage("");

      // Add message to database first
      const messageData = await api.addMessageToConversation(selectedConversation.id, {
        message: messageText,
        text: messageText,
        content: messageText,
        body: messageText,
        sender: sender,
      });

      // Get message ID from response or use temp ID
      const messageId = messageData?.id || messageData?.[0]?.id || tempMessageId;
      
      // Set status to sending
      setMessageStatuses(prev => ({ ...prev, [messageId]: 'sending' }));

      // Prepare data for WhatsApp API
      const whatsappPayload = {
        message: messageText,
        sender: {
          id: currentUser?.id || 'unknown',
          name: currentUser?.name || 'Agent',
          num: currentUser?.phone || currentUser?.email || '',
          email: currentUser?.email || ''
        },
        recipient: {
          id: selectedConversation.id?.toString() || '',
          name: selectedConversation.name || 'Customer',
          num: selectedConversation.phone || '',
          email: selectedConversation.email || ''
        }
      };

      // Send to WhatsApp API
      try {
        const response = await fetch('https://api.audo.chat/webhook/team-msg', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(whatsappPayload),
        });

        // Check if response is OK (status 200-299)
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: Failed to send message`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          }
          
          console.error("WhatsApp API error:", response.status, errorMessage);
          setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
          setMessageErrors(prev => ({ ...prev, [messageId]: errorMessage }));
          return;
        }

        const result = await response.json();

        // Handle different response formats
        // Expected response formats:
        // 1. { "status": "done" } or { "status": "success" }
        // 2. { "success": true }
        // 3. { "message": "sent" }
        // 4. HTTP 200 with empty body
        if (
          result.status === 'done' || 
          result.status === 'success' || 
          result.success === true ||
          result.message === 'sent' ||
          (response.status >= 200 && response.status < 300 && !result.error)
        ) {
          // Message sent successfully
          setMessageStatuses(prev => ({ ...prev, [messageId]: 'sent' }));
          // Clear any previous error
          setMessageErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[messageId];
            return newErrors;
          });
        } else {
          // Failed to send
          const errorMessage = result.error || result.message || 'Unknown error occurred';
          console.error("WhatsApp API returned error:", result);
          setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
          setMessageErrors(prev => ({ ...prev, [messageId]: errorMessage }));
        }
      } catch (apiError) {
        const errorMessage = apiError.message || 'Network error or server unavailable';
        console.error("WhatsApp API error:", apiError);
        setMessageStatuses(prev => ({ ...prev, [messageId]: 'failed' }));
        setMessageErrors(prev => ({ ...prev, [messageId]: errorMessage }));
      }

    } catch (error) {
      console.error("Error sending message:", error);
      setMessageStatuses(prev => ({ ...prev, [tempMessageId]: 'failed' }));
      setMessageErrors(prev => ({ ...prev, [tempMessageId]: error.message || 'Failed to send message' }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
    }
  };

  const handleBackToConversations = () => {
    setSelectedConversation(null);
    setShowConversations(true);
  };

  // Mobile Layout - WhatsApp Style
  if (isMobile) {
    return (
      <VuiBox
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: dark.body,
          zIndex: 1000,
        }}
      >
        {/* Logo Header - Always Visible */}
        <VuiBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1301,
            backgroundColor: dark.body,
            borderBottom: `1px solid ${borderCol.navbar}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            px: 1,
            py: 0.75,
            minHeight: "44px",
            height: "44px",
          }}
        >
          <VuiBox
            component="button"
            onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: "28px", sm: "32px" },
              height: { xs: "28px", sm: "32px" },
              borderRadius: "8px",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(255, 255, 255, 0.15)",
                transform: "scale(1.05)",
              },
            }}
          >
            <VuiBox
              component="img"
              src="https://www.audome.io/audome-uploads/icon.png"
              alt="AudoChat"
              sx={{
                width: { xs: "14px", sm: "18px" },
                height: { xs: "14px", sm: "18px" },
                objectFit: "contain",
              }}
            />
          </VuiBox>
        </VuiBox>

        <VuiBox
          sx={{
            height: "100vh",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            pt: "44px",
          }}
        >
          {/* Conversations List */}
          {showConversations && (
            <Card
              sx={{
                height: "100vh",
                width: "100vw",
                borderRadius: 0,
                m: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: secondary.main,
              }}
            >
              {/* Header */}
              <VuiBox
                p={2}
                sx={{
                  background: linearGradient(card.main, card.state, card.deg),
                  borderBottom: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                  <VuiTypography variant="lg" color="white" fontWeight="bold" sx={{ fontSize: "18px" }}>
                    Conversations
                  </VuiTypography>
                  <IconButton
                    size="small"
                    sx={{
                      color: text.main,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <HiOutlineDotsVertical size={20} />
                  </IconButton>
                </VuiBox>
              </VuiBox>

              {/* Search */}
              <VuiBox p={2}>
                <TextField
                  fullWidth
                  placeholder="Search conversations..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: secondary.focus,
                      color: "white",
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: borderCol.navbar,
                      },
                      "&:hover fieldset": {
                        borderColor: info.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: info.main,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "white",
                      fontSize: "14px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: text.main,
                      opacity: 0.7,
                    },
                  }}
                />
              </VuiBox>

              {/* Conversations List */}
              <VuiBox
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: secondary.main,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: borderCol.main,
                    borderRadius: "2px",
                  },
                }}
              >
                {loading ? (
                  <VuiBox p={4} display="flex" justifyContent="center">
                    <CircularProgress color="info" />
                  </VuiBox>
                ) : conversations.length > 0 ? (
                  conversations.map((conversation) => (
                  <VuiBox
                    key={conversation.id}
                    onClick={() => {
                      // Clear unread count when conversation is clicked
                      setConversations(prev =>
                        prev.map(conv =>
                          conv.id === conversation.id
                            ? { ...conv, unread: 0 }
                            : conv
                        )
                      );
                      setSelectedConversation(conversation);
                    }}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderBottom: `1px solid ${borderCol.navbar}`,
                      backgroundColor: selectedConversation?.id === conversation.id ? secondary.focus : "transparent",
                      "&:hover": {
                        backgroundColor: secondary.focus,
                      },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <VuiBox display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={conversation.avatar}
                        sx={{
                          width: 50,
                          height: 50,
                          border: `2px solid ${info.main}`,
                        }}
                      />
                      <VuiBox sx={{ flex: 1, minWidth: 0 }}>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <VuiTypography
                            variant="button"
                            color="white"
                            fontWeight="medium"
                            sx={{
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {conversation.name}
                          </VuiTypography>
                          <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "11px", ml: 1 }}>
                            {conversation.formattedTimestamp || formatTimestamp(conversation.timestamp || conversation.updated_at || conversation.created_at)}
                          </VuiTypography>
                        </VuiBox>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                          <VuiBox display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
                            <VuiTypography
                              variant="caption"
                              color={text.main}
                              sx={{
                                fontSize: "12px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "150px",
                              }}
                            >
                              {(
                                (conversation.messages && conversation.messages.length > 0)
                                  ? (conversation.messages[conversation.messages.length - 1]?.text)
                                  : conversation.last_message
                              ) || "No messages"}
                            </VuiTypography>
                            <Chip
                              icon={(conversation.aiMode || conversation.ai_mode) ? <BsRobot size={12} /> : <BsChatDots size={12} />}
                              label={(conversation.aiMode || conversation.ai_mode) ? "AI" : "Manual"}
                              size="small"
                              sx={{
                                backgroundColor: (conversation.aiMode || conversation.ai_mode) ? info.main : error.main,
                                color: "white",
                                height: "18px",
                                fontSize: "9px",
                                minWidth: "50px",
                                fontWeight: "bold",
                                "& .MuiChip-icon": {
                                  color: "white",
                                  marginLeft: "4px",
                                },
                                "& .MuiChip-label": {
                                  color: "white",
                                  paddingLeft: "4px",
                                  paddingRight: "4px",
                                },
                              }}
                            />
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>
                  ))
                ) : (
                  <VuiBox p={4} textAlign="center">
                    <VuiTypography variant="h6" color={text.main}>
                      No conversations yet
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          )}

          {/* Chat Window */}
          {selectedConversation && !showConversations && (
            <Card
              sx={{
                height: "100vh",
                width: "100vw",
                borderRadius: 0,
                m: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: dark.body,
              }}
            >
              {/* Chat Header */}
              <VuiBox
                p={2}
                sx={{
                  background: linearGradient(card.main, card.state, card.deg),
                  borderBottom: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                  <VuiBox display="flex" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <IconButton
                      onClick={handleBackToConversations}
                      sx={{
                        color: text.main,
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      <IoArrowBack size={22} />
                    </IconButton>
                    <Avatar
                      src={selectedConversation.avatar}
                      sx={{
                        width: 42,
                        height: 42,
                        border: `2px solid ${info.main}`,
                        flexShrink: 0,
                      }}
                    />
                    <VuiBox sx={{ flex: 1, minWidth: 0 }}>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="medium" 
                        sx={{ 
                          fontSize: "15px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedConversation.name}
                      </VuiTypography>
                      <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "11px" }}>
                        Online
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <Chip
                      icon={isAIMode ? <BsRobot size={16} /> : <BsChatDots size={16} />}
                      label={isAIMode ? "AI" : "Manual"}
                      onClick={async () => {
                        const newAIMode = !isAIMode;
                        setIsAIMode(newAIMode);
                        if (selectedConversation) {
                          // Only update if aiMode actually changed
                          if (selectedConversation.aiMode !== newAIMode) {
                            try {
                              await api.updateConversationAIMode(selectedConversation.id, newAIMode);
                              const updated = { ...selectedConversation, aiMode: newAIMode, ai_mode: newAIMode };
                              setSelectedConversation(updated);
                              // Don't reload conversations - real-time subscription will handle the update
                            } catch (error) {
                              console.error("Error updating AI mode:", error);
                              alert("Failed to update AI mode: " + error.message);
                            }
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: isAIMode ? info.main : error.main,
                        color: "white",
                        cursor: "pointer",
                        fontSize: "12px",
                        height: "32px",
                        fontWeight: "bold",
                        border: `2px solid ${isAIMode ? info.focus : error.focus}`,
                        "&:hover": {
                          backgroundColor: isAIMode ? info.focus : error.focus,
                          borderColor: error.main,
                        },
                        "& .MuiChip-icon": {
                          color: "white",
                          marginLeft: "6px",
                        },
                        "& .MuiChip-label": {
                          color: "white",
                          fontWeight: "bold",
                          paddingLeft: "6px",
                          paddingRight: "8px",
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        color: text.main,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      <HiOutlineDotsVertical />
                    </IconButton>
                  </VuiBox>
                </VuiBox>
              </VuiBox>

              {/* Messages */}
              <VuiBox
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  backgroundColor: dark.body,
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: dark.body,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: borderCol.main,
                    borderRadius: "2px",
                  },
                }}
              >
                {selectedConversation.messages && Array.isArray(selectedConversation.messages) && selectedConversation.messages.length > 0 ? (
                  selectedConversation.messages.map((msg, index, messagesArray) => {
                    // User messages go to the right, Agent/AI messages go to the left
                    const isUser = msg.sender === "user";
                    const isAgent = msg.sender === "agent" || msg.sender === "ai";
                    const isLastMessage = index === messagesArray.length - 1;
                    return (
                      <VuiBox
                        key={msg.id || Math.random()}
                        sx={{
                          display: "flex",
                          justifyContent: isUser ? "flex-end" : "flex-start", // User messages right (flex-end), Agent/AI messages left (flex-start)
                          mb: 2,
                        }}
                      >
                        <VuiBox
                          sx={{
                            maxWidth: "75%",
                            p: 1.5,
                            borderRadius: "12px",
                            backgroundColor: 
                              msg.sender === "user"
                                ? success.main  // Green for user messages
                                : msg.sender === "ai"
                                ? info.main     // Blue for AI messages
                                : orange.main,  // Orange for agent messages
                            color: "white",
                            position: "relative",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {isAgent && msg.sender === "ai" && (
                            <VuiBox
                              sx={{
                                position: "absolute",
                                top: -8,
                                left: 8,
                                backgroundColor: info.main,
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: `2px solid ${dark.body}`,
                              }}
                            >
                              <BsRobot size={12} color="white" />
                            </VuiBox>
                          )}
                          <VuiTypography variant="body2" color="white" sx={{ fontSize: "14px", lineHeight: 1.5, wordBreak: "break-word" }}>
                            {msg.text || ''}
                          </VuiTypography>
                          <VuiBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {msg.time && (
                              <VuiTypography
                                variant="caption"
                                sx={{
                                  color: "rgba(255, 255, 255, 0.7)",
                                  fontSize: "10px",
                                }}
                              >
                                {msg.time}
                              </VuiTypography>
                            )}
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    );
                  })
                ) : (
                  <VuiBox
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      color: text.main,
                    }}
                  >
                    <VuiTypography variant="body2" color="text">
                      No messages yet. Start the conversation!
                    </VuiTypography>
                  </VuiBox>
                )}
                <div ref={messagesEndRef} />
              </VuiBox>

              {/* Message Input */}
              <VuiBox
                p={2}
                sx={{
                  background: linearGradient(card.main, card.state, card.deg),
                  borderTop: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiBox display="flex" alignItems="flex-end" gap={1}>
                  <TextField
                    fullWidth
                    placeholder={isAIMode ? "AI is responding..." : "Type a message..."}
                    value={message}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setMessage(newValue);
                      // Detect direction based on content
                      setInputDirection(getTextDirection(newValue));
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={isAIMode}
                    multiline
                    maxRows={4}
                    dir={inputDirection}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: secondary.focus,
                        color: "white",
                        borderRadius: "12px",
                        direction: inputDirection,
                        "& fieldset": {
                          borderColor: borderCol.navbar,
                        },
                        "&:hover fieldset": {
                          borderColor: info.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: info.main,
                        },
                        "&.Mui-disabled": {
                          backgroundColor: secondary.main,
                          opacity: 0.6,
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        fontSize: "14px",
                        direction: inputDirection,
                        textAlign: inputDirection === 'rtl' ? 'right' : 'left',
                        fontFamily: inputDirection === 'rtl' 
                          ? '"Tajawal", "Cairo", "Roboto", sans-serif' 
                          : '"Roboto", sans-serif',
                        fontWeight: inputDirection === 'rtl' ? 700 : 400,
                        "&::placeholder": {
                          color: text.main,
                          opacity: 0.7,
                        },
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={isAIMode || !message.trim()}
                    sx={{
                      backgroundColor: info.main,
                      color: "white",
                      width: "44px",
                      height: "44px",
                      "&:hover": {
                        backgroundColor: info.focus,
                      },
                      "&:disabled": {
                        backgroundColor: secondary.focus,
                        color: text.main,
                        opacity: 0.5,
                      },
                    }}
                  >
                    <IoMdSend size={20} />
                  </IconButton>
                </VuiBox>
                {isAIMode && (
                  <VuiBox mt={1} display="flex" alignItems="center" gap={1}>
                    <BsRobot size={16} color={info.main} />
                    <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "11px" }}>
                      AI is handling this conversation automatically
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          )}
        </VuiBox>
      </VuiBox>
    );
  }

  // Desktop Layout
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Grid container spacing={3} sx={{ height: "calc(100vh - 240px)" }}>
          {/* Conversations List */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Header */}
              <VuiBox
                p={2}
                sx={{
                  background: linearGradient(card.main, card.state, card.deg),
                  borderBottom: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                  <VuiTypography variant="lg" color="white" fontWeight="bold">
                    Conversations
                  </VuiTypography>
                  <IconButton
                    size="small"
                    sx={{
                      color: text.main,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <HiOutlineDotsVertical />
                  </IconButton>
                </VuiBox>
              </VuiBox>

              {/* Search */}
              <VuiBox p={2}>
                <TextField
                  fullWidth
                  placeholder="Search conversations..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: secondary.focus,
                      color: "white",
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: borderCol.navbar,
                      },
                      "&:hover fieldset": {
                        borderColor: info.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: info.main,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "white",
                      fontSize: "16px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: text.main,
                      opacity: 0.7,
                    },
                  }}
                />
              </VuiBox>

              {/* Conversations */}
              <VuiBox
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: secondary.main,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: borderCol.main,
                    borderRadius: "3px",
                  },
                }}
              >
                {loading ? (
                  <VuiBox p={4} display="flex" justifyContent="center">
                    <CircularProgress color="info" />
                  </VuiBox>
                ) : conversations.length > 0 ? (
                  conversations.map((conversation) => (
                  <VuiBox
                    key={conversation.id}
                    onClick={() => {
                      // Clear unread count when conversation is clicked
                      setConversations(prev =>
                        prev.map(conv =>
                          conv.id === conversation.id
                            ? { ...conv, unread: 0 }
                            : conv
                        )
                      );
                      setSelectedConversation(conversation);
                    }}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderBottom: `1px solid ${borderCol.navbar}`,
                      backgroundColor: selectedConversation?.id === conversation.id ? secondary.focus : "transparent",
                      "&:hover": {
                        backgroundColor: secondary.focus,
                      },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <VuiBox display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={conversation.avatar}
                        sx={{
                          width: 50,
                          height: 50,
                          border: `2px solid ${info.main}`,
                        }}
                      />
                      <VuiBox sx={{ flex: 1, minWidth: 0 }}>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <VuiTypography
                            variant="button"
                            color="white"
                            fontWeight="medium"
                            sx={{
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {conversation.name}
                          </VuiTypography>
                          <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "12px", ml: 1 }}>
                            {conversation.formattedTimestamp || formatTimestamp(conversation.timestamp || conversation.updated_at || conversation.created_at)}
                          </VuiTypography>
                        </VuiBox>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                          <VuiBox display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
                            <VuiTypography
                              variant="caption"
                              color={text.main}
                              sx={{
                                fontSize: "12px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "180px",
                              }}
                            >
                              {(
                                (conversation.messages && conversation.messages.length > 0)
                                  ? (conversation.messages[conversation.messages.length - 1]?.text)
                                  : conversation.last_message
                              ) || "No messages"}
                            </VuiTypography>
                            <Chip
                              icon={(conversation.aiMode || conversation.ai_mode) ? <BsRobot size={12} /> : <BsChatDots size={12} />}
                              label={(conversation.aiMode || conversation.ai_mode) ? "AI" : "Manual"}
                              size="small"
                              sx={{
                                backgroundColor: (conversation.aiMode || conversation.ai_mode) ? info.main : error.main,
                                color: "white",
                                height: "18px",
                                fontSize: "9px",
                                minWidth: "50px",
                                fontWeight: "bold",
                                "& .MuiChip-icon": {
                                  color: "white",
                                  marginLeft: "4px",
                                },
                                "& .MuiChip-label": {
                                  color: "white",
                                  paddingLeft: "4px",
                                  paddingRight: "4px",
                                },
                              }}
                            />
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>
                  ))
                ) : (
                  <VuiBox p={4} textAlign="center">
                    <VuiTypography variant="h6" color={text.main}>
                      No conversations yet
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </Grid>

          {/* Chat Window */}
          <Grid item xs={12} md={8}>
            {selectedConversation ? (
              <Card sx={{ height: "calc(100vh - 200px)", display: "flex", flexDirection: "column", minHeight: 0 }}>
                {/* Chat Header */}
                <VuiBox
                  p={2}
                  sx={{
                    background: linearGradient(card.main, card.state, card.deg),
                    borderBottom: `1px solid ${borderCol.navbar}`,
                    flexShrink: 0,
                  }}
                >
                  <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                    <VuiBox display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={selectedConversation.avatar}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${info.main}`,
                        }}
                      />
                      <VuiBox>
                        <VuiTypography variant="button" color="white" fontWeight="medium" sx={{ fontSize: "14px" }}>
                          {selectedConversation.name}
                        </VuiTypography>
                        <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "12px" }}>
                          Online
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                    <VuiBox display="flex" alignItems="center" gap={1}>
                      <Chip
                        icon={isAIMode ? <BsRobot size={16} /> : <BsChatDots size={16} />}
                        label={isAIMode ? "AI" : "Manual"}
                        onClick={async () => {
                          const newAIMode = !isAIMode;
                          setIsAIMode(newAIMode);
                          if (selectedConversation) {
                            // Only update if aiMode actually changed
                            if (selectedConversation.aiMode !== newAIMode) {
                              try {
                                await api.updateConversationAIMode(selectedConversation.id, newAIMode);
                                const updated = { ...selectedConversation, aiMode: newAIMode, ai_mode: newAIMode };
                                setSelectedConversation(updated);
                                await loadConversations(); // Reload to update in list
                              } catch (error) {
                                console.error("Error updating AI mode:", error);
                                alert("Failed to update AI mode: " + error.message);
                              }
                            }
                          }
                        }}
                        sx={{
                          backgroundColor: isAIMode ? info.main : error.main,
                          color: "white",
                          cursor: "pointer",
                          fontSize: "13px",
                          height: "36px",
                          fontWeight: "bold",
                          border: `2px solid ${isAIMode ? info.focus : error.focus}`,
                          "&:hover": {
                            backgroundColor: isAIMode ? info.focus : error.focus,
                            borderColor: error.main,
                          },
                          "& .MuiChip-icon": {
                            color: "white",
                            marginLeft: "6px",
                          },
                          "& .MuiChip-label": {
                            color: "white",
                            fontWeight: "bold",
                            paddingLeft: "6px",
                            paddingRight: "8px",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          color: text.main,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                          },
                        }}
                      >
                        <HiOutlineDotsVertical />
                      </IconButton>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>

                {/* Messages */}
                <VuiBox
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    p: 2,
                    backgroundColor: dark.body,
                    minHeight: 0,
                    maxHeight: "100%",
                    border: `1px solid ${borderCol.navbar}`,
                    borderRadius: "8px",
                    margin: "8px",
                    display: "flex",
                    flexDirection: "column",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: dark.body,
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: borderCol.main,
                      borderRadius: "4px",
                      "&:hover": {
                        background: info.main,
                      },
                    },
                  }}
                >
                  {selectedConversation.messages && Array.isArray(selectedConversation.messages) && selectedConversation.messages.length > 0 ? (
                    selectedConversation.messages.map((msg, index, messagesArray) => {
                      // User messages go to the right, Agent/AI messages go to the left
                      const isUser = msg.sender === "user";
                      const isAgent = msg.sender === "agent" || msg.sender === "ai";
                      const isLastMessage = index === messagesArray.length - 1;
                      // Debug: log sender for troubleshooting
                      if (index === 0) console.log('Message sender check:', { text: msg.text?.substring(0, 20), sender: msg.sender, isUser, isAgent });
                      return (
                        <VuiBox
                          key={msg.id || Math.random()}
                          sx={{
                            display: "flex",
                            justifyContent: isUser ? "flex-end" : "flex-start", // User messages right (flex-end), Agent/AI messages left (flex-start)
                            mb: 2,
                          }}
                        >
                          <VuiBox
                            sx={{
                              maxWidth: "70%",
                              p: 1.5,
                              borderRadius: "12px",
                              backgroundColor: 
                                msg.sender === "user"
                                  ? success.main  // Green for user messages
                                  : msg.sender === "ai"
                                  ? info.main     // Blue for AI messages
                                  : orange.main,  // Orange for agent messages
                              color: "white",
                              position: "relative",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            {isAgent && msg.sender === "ai" && (
                              <VuiBox
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  left: 8,
                                  backgroundColor: info.main,
                                  borderRadius: "50%",
                                  width: "24px",
                                  height: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: `2px solid ${dark.body}`,
                                }}
                              >
                                <BsRobot size={12} color="white" />
                              </VuiBox>
                            )}
                            <VuiTypography 
                              variant="body2" 
                              color="white" 
                              dir={containsArabic(msg.text || '') ? 'rtl' : 'ltr'}
                              component="div"
                              sx={{ 
                                fontSize: "14px", 
                                lineHeight: 1.5, 
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap", // Preserve line breaks (\n)
                                direction: containsArabic(msg.text || '') ? 'rtl' : 'ltr',
                                textAlign: containsArabic(msg.text || '') ? 'right' : 'left',
                                fontFamily: containsArabic(msg.text || '') 
                                  ? '"Tajawal", "Cairo", "Roboto", sans-serif' 
                                  : '"Roboto", sans-serif',
                                fontWeight: containsArabic(msg.text || '') ? 700 : 400,
                              }}
                            >
                              {msg.text || ''}
                            </VuiTypography>
                            <VuiBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              {msg.time && (
                                <VuiTypography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(255, 255, 255, 0.7)",
                                    fontSize: "10px",
                                  }}
                                >
                                  {msg.time}
                                </VuiTypography>
                              )}
                              {/* Message status icons (WhatsApp style) - only for agent/ai messages */}
                              {isAgent && (() => {
                                const status = messageStatuses[msg.id] || 'sent'; // Default to 'sent' for old messages
                                if (status === 'sending') {
                                  return (
                                    <MdSchedule 
                                      size={14} 
                                      color="rgba(255, 255, 255, 0.7)" 
                                      style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
                                    />
                                  );
                                } else if (status === 'sent') {
                                  return (
                                    <MdDone 
                                      size={14} 
                                      color="rgba(255, 255, 255, 0.7)" 
                                    />
                                  );
                                } else if (status === 'failed') {
                                  const errorMsg = messageErrors[msg.id];
                                  return (
                                    <VuiBox
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.8,
                                        },
                                      }}
                                      onClick={() => {
                                        if (msg.text) {
                                          handleRetryMessage(msg.id, msg.text, msg.sender);
                                        }
                                      }}
                                      title={errorMsg ? `خطأ: ${errorMsg}\nانقر لإعادة المحاولة` : 'انقر لإعادة المحاولة'}
                                    >
                                      <MdError 
                                        size={14} 
                                        color="rgba(255, 0, 0, 0.8)" 
                                      />
                                      {errorMsg && (
                                        <VuiTypography
                                          variant="caption"
                                          sx={{
                                            color: "rgba(255, 0, 0, 0.8)",
                                            fontSize: "9px",
                                            maxWidth: "100px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {errorMsg.substring(0, 20)}...
                                        </VuiTypography>
                                      )}
                                    </VuiBox>
                                  );
                                }
                                // Default: double check (message delivered)
                                return (
                                  <MdDoneAll 
                                    size={14} 
                                      color="rgba(255, 255, 255, 0.9)" 
                                    />
                                );
                              })()}
                            </VuiBox>
                          </VuiBox>
                        </VuiBox>
                      );
                    })
                  ) : (
                    <VuiBox
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        color: text.main,
                      }}
                    >
                      <VuiTypography variant="body2" color="text">
                        No messages yet. Start the conversation!
                      </VuiTypography>
                    </VuiBox>
                  )}
                  <div ref={messagesEndRef} />
                </VuiBox>

                {/* Message Input */}
                <VuiBox
                  p={2}
                  sx={{
                    background: linearGradient(card.main, card.state, card.deg),
                    borderTop: `1px solid ${borderCol.navbar}`,
                    flexShrink: 0,
                  }}
                >
                  {!canReplyToConversation() && (
                    <VuiBox
                      mb={2}
                      p={1.5}
                      sx={{
                        backgroundColor: "rgba(227, 26, 26, 0.2)",
                        borderRadius: "8px",
                        border: "1px solid rgba(227, 26, 26, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <VuiTypography variant="caption" color="error" fontWeight="medium" sx={{ fontSize: "12px" }}>
                        ⚠️ WhatsApp Restriction: Cannot reply - Last customer message was sent more than 24 hours ago. Use a template message to start a new conversation.
                      </VuiTypography>
                    </VuiBox>
                  )}
                  <VuiBox display="flex" alignItems="flex-end" gap={1}>
                    <TextField
                      fullWidth
                      placeholder={
                        !canReplyToConversation() 
                          ? "Use template message to reply..." 
                          : isAIMode 
                          ? "AI is responding..." 
                          : "Type a message..."
                      }
                      value={message}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setMessage(newValue);
                        // Detect direction based on content
                        setInputDirection(getTextDirection(newValue));
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={isAIMode || !canReplyToConversation()}
                      multiline
                      maxRows={4}
                      dir={inputDirection}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: secondary.focus,
                          color: "white",
                          borderRadius: "12px",
                          direction: inputDirection,
                          "& fieldset": {
                            borderColor: !canReplyToConversation() ? error.main : borderCol.navbar,
                          },
                          "&:hover fieldset": {
                            borderColor: !canReplyToConversation() ? error.main : info.main,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: !canReplyToConversation() ? error.main : info.main,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: secondary.main,
                            opacity: 0.6,
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "white",
                          fontSize: "16px",
                          direction: inputDirection,
                          textAlign: inputDirection === 'rtl' ? 'right' : 'left',
                          fontFamily: inputDirection === 'rtl' 
                            ? '"Tajawal", "Cairo", "Roboto", sans-serif' 
                            : '"Roboto", sans-serif',
                          fontWeight: inputDirection === 'rtl' ? 700 : 400,
                          "&::placeholder": {
                            color: !canReplyToConversation() ? error.main : text.main,
                            opacity: 0.7,
                          },
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={isAIMode || !message.trim() || !canReplyToConversation()}
                      sx={{
                        backgroundColor: !canReplyToConversation() ? error.main : info.main,
                        color: "white",
                        width: "48px",
                        height: "48px",
                        "&:hover": {
                          backgroundColor: !canReplyToConversation() ? error.focus : info.focus,
                        },
                        "&:disabled": {
                          backgroundColor: secondary.focus,
                          color: text.main,
                          opacity: 0.5,
                        },
                      }}
                    >
                      <IoMdSend size={20} />
                    </IconButton>
                  </VuiBox>
                  {isAIMode && (
                    <VuiBox mt={1} display="flex" alignItems="center" gap={1}>
                      <BsRobot size={16} color={info.main} />
                      <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "12px" }}>
                        AI is handling this conversation automatically
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              </Card>
            ) : (
              <Card sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <VuiBox display="flex" flexDirection="column" alignItems="center" p={3}>
                  <BsChatDots size={64} color={text.main} style={{ opacity: 0.3 }} />
                  <VuiTypography variant="h6" color={text.main} mt={2} textAlign="center">
                    Select a conversation to start chatting
                  </VuiTypography>
                </VuiBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Chat;

